/**
 * Score Badge Component
 * Exibe o score de um lead com cor e ícone apropriado
 */

import { Flame, Thermometer, Snowflake } from "lucide-react";
import { Badge } from "./ui/badge";
import { resolveTemperature, TEMPERATURE_LABELS, getSignalDefinition, type LeadTemperature } from "@shared/lead-scoring";

// Aparência por temperatura. As FRONTEIRAS não moram aqui — vêm de
// shared/lead-scoring.ts. Antes este arquivo tinha sua própria cópia dos
// limiares (`> 50` / `>= 25`), replicada em outros quatro lugares e divergente
// da usada pelo servidor: um lead com exatamente 50 pontos era "warm" no badge
// e "hot" na lista de hot leads.
const TEMPERATURE_STYLES: Record<LeadTemperature, { color: string; icon: typeof Flame; description: string }> = {
  hot: { color: "bg-red-100 text-red-800", icon: Flame, description: "Lead muito qualificado" },
  warm: { color: "bg-amber-100 text-amber-800", icon: Thermometer, description: "Lead em desenvolvimento" },
  cold: { color: "bg-blue-100 text-blue-800", icon: Snowflake, description: "Lead em estágio inicial" },
};

interface ScoreBadgeProps {
  score: number;
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
}

export function ScoreBadge({ score, size = "md", showLabel = true }: ScoreBadgeProps) {
  const temperature = resolveTemperature(score);
  const info = { ...TEMPERATURE_STYLES[temperature], label: TEMPERATURE_LABELS[temperature] };
  const Icon = info.icon;

  const sizeClasses = {
    sm: "text-xs px-2 py-1",
    md: "text-sm px-3 py-1.5",
    lg: "text-base px-4 py-2",
  };

  return (
    <div className="flex items-center gap-2">
      <Badge className={`${info.color} ${sizeClasses[size]} flex items-center gap-1`}>
        <Icon className={size === "sm" ? "w-3 h-3" : size === "md" ? "w-4 h-4" : "w-5 h-5"} />
        {showLabel && info.label}
      </Badge>
      {showLabel && (
        <span className="text-xs text-gray-600" title={info.description}>
          {score} pts
        </span>
      )}
    </div>
  );
}

/**
 * Score Progress Component
 * Exibe o score como uma barra de progresso
 */
interface ScoreProgressProps {
  score: number;
  maxScore?: number;
  showLabel?: boolean;
}

export function ScoreProgress({ score, maxScore = 100, showLabel = true }: ScoreProgressProps) {
  const percentage = Math.min((score / maxScore) * 100, 100);

  // A cor segue a temperatura do SCORE, não a porcentagem da barra: com
  // maxScore != 100 as duas divergiam e a barra ficava vermelha para um lead
  // frio (ex.: 30 pontos num máximo de 50).
  const barColor = { hot: "bg-red-500", warm: "bg-amber-500", cold: "bg-blue-500" }[
    resolveTemperature(score)
  ];

  return (
    <div className="space-y-2">
      {showLabel && (
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium">Score</span>
          <span className="text-sm font-bold">{score}/{maxScore}</span>
        </div>
      )}
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all ${barColor}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Score Card Component
 * Card compacto para exibir score e informações relacionadas
 */
interface ScoreCardProps {
  score: number;
  contactName?: string;
  lastActivity?: Date | null;
  breakdown?: Record<string, number> | null;
}

export function ScoreCard({ score, contactName, lastActivity, breakdown }: ScoreCardProps) {
  const scoreLabel = `Lead ${TEMPERATURE_LABELS[resolveTemperature(score)]}`;

  return (
    <div className="border rounded-lg p-4 bg-white">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-medium text-gray-600">Score</h3>
          <p className="text-2xl font-bold text-gray-900">{score}</p>
        </div>
        <ScoreBadge score={score} size="lg" showLabel={false} />
      </div>
      
      <div className="space-y-2 text-sm">
        <div>
          <span className="text-gray-600">{scoreLabel}</span>
        </div>
        
        {lastActivity && (
          <div className="text-gray-500">
            Última atividade: {new Date(lastActivity).toLocaleDateString("pt-BR")}
          </div>
        )}
        
        {breakdown && Object.keys(breakdown).length > 0 && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-xs font-medium text-gray-600 mb-2">De onde vêm os pontos:</p>
            <div className="space-y-1">
              {/* Ordena por MAGNITUDE: com pontuação negativa (descadastro,
                  spam) a ordenação crescente escondia justamente os sinais
                  mais relevantes no fim da lista. */}
              {Object.entries(breakdown)
                .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
                .slice(0, 3)
                .map(([event, points]) => (
                  <div key={event} className="flex justify-between text-xs">
                    <span className="text-gray-600">
                      {getSignalDefinition(event)?.label ?? event}
                    </span>
                    <span className={`font-medium ${points < 0 ? "text-red-600" : ""}`}>
                      {points > 0 ? `+${points}` : points}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
